'use strict';
import DataQueryEnums from './DataQueryEnumerations';
const DisplayImageEnum = DataQueryEnums.DisplayImageEnum;

DataQueryRowClassFactory.$inject = ['$q', '$mdUtil'];

export default function DataQueryRowClassFactory($q, $mdUtil) {
	function DataQueryRow(entity, displayEntity, displayEntityGetter) {
		this._entity = entity || {};
		this._notChangedEntity = angular.copy(this._entity);

		this._displayEntity = displayEntity || {};

		this._changedColumns = [];

		this._debouncedDisplayEntityGetter = $mdUtil.debounce(
			function () {
				var changedColumns = this._changedColumns;
				var defer = this._displayEntityDefer;
				delete this._displayEntityDefer;

				if (typeof displayEntityGetter === 'function') {
					displayEntityGetter(this._entity, this._displayEntity, changedColumns)
						.then(function (displayEntity) {
							defer.resolve(displayEntity);
						})
						.catch(function () {
							defer.reject();
						});
				} else {
					defer.resolve(this._displayEntity);
				}
			}.bind(this),
			400
		);
	}

	DataQueryRow.prototype = {
		getEntity: function () {
			return this._entity;
		},

		setEntity: function (newEntity) {
			var changedColumns;

			if (newEntity !== this._entity) {
				changedColumns = this._columns;
			} else {
				changedColumns = this._columns.filter(function (column) {
					if (column.RawValuePropertyName) {
						return newEntity[column.RawValuePropertyName] !== this._notChangedEntity[column.RawValuePropertyName];
					} else {
						return true;
					}
				}, this);
			}

			Object.keys(newEntity).forEach(function (key) {
				this._entity[key] = newEntity[key];
			}, this);

			this._notChangedEntity = angular.copy(this._entity);

			return this._updateDisplayEntity(changedColumns);
		},

		initProperties: function (columns) {
			this._columns = columns;

			return this._loadDefaultDisplayValues().then(
				function () {
					columns.forEach(function (column) {
						this._initProperty(column);
					}, this);
				}.bind(this)
			);
		},

		_updateDisplayEntity: function (changedColumns) {
			if (!this._displayEntityDefer) {
				this._displayEntityDefer = $q.defer();
				this._changedColumns = [];

				this._displayEntityPromise = this._displayEntityDefer.promise.then(
					function (displayEntity) {
						this._displayEntity = displayEntity;
					}.bind(this)
				);
			}

			changedColumns.forEach(function (column) {
				if (this._changedColumns.indexOf(column) < 0) {
					this._changedColumns.push(column);
				}
			}, this);

			if (this._changedColumns.length) {
				this._debouncedDisplayEntityGetter();
				return this._displayEntityPromise;
			} else {
				return $q.when(this._displayEntity);
			}
		},

		_normalizeRawValue: function (value, column) {
			switch (column.AttributeValueType) {
				case mx.shell.ObjectPropertyType.Boolean.name:
					return Boolean(value);
				default:
					return value;
			}
		},

		getEntityValue: function (field) {
			return this[field + '_Value'];
		},

		getEntityDisplayValue: function (field) {
			return this[field];
		},

		setEntityValue: function (field, value, displayValue) {
			var column = this._columns.filter(function (column) {
				return column.Name === field;
			})[0];

			if (column) {
				return this._setEntityValue(column, value, displayValue);
			} else {
				return $q.reject('column with Name = ' + field + ' was not found');
			}
		},

		_setEntityValue: function (column, value, displayValue) {
			// if property name of raw object was not defined then it is impossible to change raw entity
			// and there is no sense to change displayEntity
			if (typeof column.RawValuePropertyName === 'undefined') {
				return;
			}

			value = this._normalizeRawValue(value, column);

			if (this._entity[column.RawValuePropertyName] === value) {
				if (column.IsSingleColumn && this._entity[column.RawValuePropertyName] !== this._displayEntity[column.Name]) {
					this._displayEntity[column.Name] = this._entity[column.RawValuePropertyName];
				}
				return;
			}
			this._entity[column.RawValuePropertyName] = value;

			this._notChangedEntity = angular.copy(this._entity);

			if (displayValue) {
				this._displayEntity[column.Name + '_Value'] = value;
				this._displayEntity[column.Name] = displayValue;

				return $q.resolve(this._displayEntity);
			} else {
				return this._updateDisplayEntity([column]);
			}
		},

		_rawValueHasChangedCalcValue: function (propertyName) {
			//check if rawEntity has calc-filed for property 'propertyName'
			return typeof this._entity[propertyName + mx.SolutionBuilderAgent.DataModelCalculatedProperty.prototype.constants.calcValueSuffix] === 'object';
		},

		_loadDefaultDisplayValues: function () {
			// Try to get displayValues for all columns in cases:
			// 1. if column doesn't have current displayValue - ex. can be used for enums in dynamic-grid
			// 2. if calc-property for column.RawValuePropertyName was created - then property of RawEntity could be modified, so it should be reloaded
			// 3. if calc-property for column.Name was created - then grid should use this calc-property and not from expressions, so it should be reloaded
			var columns = this._columns.filter(function (column) {
				return (
					typeof this._displayEntity[column.Name] === 'undefined' ||
					(column.RawValuePropertyName && this._rawValueHasChangedCalcValue(column.RawValuePropertyName)) ||
					(!column.RawValuePropertyName && this._rawValueHasChangedCalcValue(column.Name))
				);
			}, this);

			return this._updateDisplayEntity(columns);
		},

		_initProperty: function (column) {
			var valuePropertyName = column.Name + '_Value';

			Object.defineProperty(this, column.Name, {
				enumerable: true,
				configurable: true,
				get: function () {
					return this._displayEntity[column.Name];
				},
				set: function (value) {
					if (column.IsSingleColumn) {
						this._setEntityValue(column, value, value);
					}
				},
			});

			Object.defineProperty(this, valuePropertyName, {
				enumerable: true,
				configurable: true,
				get: function () {
					if (column.IsSingleColumn) {
						return this._displayEntity[column.Name];
					} else {
						return this._displayEntity[valuePropertyName];
					}
				},
				set: function (value) {
					this._setEntityValue(column, value, column.IsSingleColumn ? value : undefined);
				},
			});

			if (column.SortingExpression) {
				var orderColumnName = column.Name + '_Order';
				Object.defineProperty(this, orderColumnName, {
					enumerable: true,
					configurable: true,
					get: function () {
						return this._displayEntity[orderColumnName];
					},
					set: function (value) {
						this._displayEntity[orderColumnName] = value;
					},
				});
			}

			if (column.DisplayImage === DisplayImageEnum.Image || column.DisplayImage === DisplayImageEnum.ImageWithText) {
				const imageColumnName = column.Name + '_Image';
				Object.defineProperty(this, imageColumnName, {
					enumerable: true,
					configurable: true,
					get: function () {
						return this._displayEntity[imageColumnName];
					},
					set: function (value) {
						this._displayEntity[imageColumnName] = value;
					},
				});
			}
		},
	};

	return DataQueryRow;
}
